import React from 'react'
import '@/app/ui/components/Toggle.css'

// Toggle Sizes
type ToggleSize = 'xs' | 'sm' | 'md' | 'lg'

// Toggle Colors
type ToggleColor = 'primary' | 'success' | 'danger' | 'warning' | 'info'

// Simple Toggle (Basic switch) - Used in tables
interface SimpleToggleProps {
  checked: boolean
  onChange: (checked: boolean) => void
  disabled?: boolean
}

export function Toggle({ checked, onChange, disabled = false }: SimpleToggleProps) {
  return (
    <label className="toggle-switch">
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        disabled={disabled}
      />
      <span className="toggle-slider"></span>
    </label>
  )
}

// Advanced Toggle (With sizes, colors, labels, icons)
interface ToggleUIProps {
  checked?: boolean
  onChange?: (checked: boolean) => void
  disabled?: boolean
  size?: ToggleSize
  color?: ToggleColor
  label?: string
  labelPosition?: 'left' | 'right'
  description?: string
  showIcon?: boolean
}

export function ToggleUI({ 
  checked = false, 
  onChange, 
  disabled = false, 
  size = 'md',
  color = 'primary',
  label,
  labelPosition = 'right',
  description,
  showIcon = false
}: ToggleUIProps) {
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!disabled && onChange) {
      onChange(e.target.checked)
    }
  }

  const sizeClasses = {
    xs: 'toggle--xs',
    sm: 'toggle--sm',
    md: 'toggle--md',
    lg: 'toggle--lg'
  }

  const colorClasses = {
    primary: 'toggle--primary',
    success: 'toggle--success',
    danger: 'toggle--danger',
    warning: 'toggle--warning',
    info: 'toggle--info'
  }

  return (
    <label className={`toggle-wrapper ${disabled ? 'toggle--disabled' : ''}`}>
      {label && labelPosition === 'left' && (
        <span className="toggle-label-container toggle-label--left">
          <span className="toggle-label">{label}</span>
          {description && <span className="toggle-description">{description}</span>}
        </span>
      )}
      <span className={`toggle ${sizeClasses[size]} ${colorClasses[color]} ${checked ? 'toggle--checked' : ''} ${disabled ? 'toggle--disabled' : ''} ${showIcon ? 'toggle--with-icon' : ''}`}>
        <input
          type="checkbox"
          checked={checked}
          onChange={handleChange}
          disabled={disabled}
          className="toggle-input"
        />
        <span className="toggle-track">
          {showIcon && (
            <>
              <span className="toggle-track-icon toggle-track-icon--check">
                <i className="fa-solid fa-check" />
              </span>
              <span className="toggle-track-icon toggle-track-icon--x">
                <i className="fa-solid fa-xmark" />
              </span>
            </>
          )}
          <span className="toggle-thumb" />
        </span>
      </span>
      {label && labelPosition === 'right' && (
        <span className="toggle-label-container toggle-label--right">
          <span className="toggle-label">{label}</span>
          {description && <span className="toggle-description">{description}</span>}
        </span>
      )}
    </label>
  )
}

// Switch Component (Alternative style with ON/OFF labels)
interface SwitchProps {
  checked?: boolean
  onChange?: (checked: boolean) => void
  disabled?: boolean
  size?: ToggleSize
  color?: ToggleColor
  onLabel?: string
  offLabel?: string
}

export function Switch({
  checked = false,
  onChange,
  disabled = false,
  size = 'md',
  color = 'primary',
  onLabel = 'ON',
  offLabel = 'OFF'
}: SwitchProps) {
  const handleChange = () => {
    if (!disabled && onChange) {
      onChange(!checked)
    }
  }

  return (
    <div 
      className={`switch switch--${size} switch--${color} ${checked ? 'switch--checked' : ''} ${disabled ? 'switch--disabled' : ''}`}
      onClick={handleChange}
      role="switch"
      aria-checked={checked}
      tabIndex={disabled ? -1 : 0}
    >
      <span className="switch-label switch-label--off">{offLabel}</span>
      <span className="switch-label switch-label--on">{onLabel}</span>
      <div className="switch-thumb" />
    </div>
  )
}

// Checkbox Toggle (Styled checkbox)
interface CheckboxToggleProps {
  checked?: boolean
  onChange?: (checked: boolean) => void
  disabled?: boolean
  label?: string
  description?: string
  indeterminate?: boolean
}

export function CheckboxToggle({
  checked = false,
  onChange,
  disabled = false,
  label,
  description,
  indeterminate = false
}: CheckboxToggleProps) {
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!disabled && onChange) {
      onChange(e.target.checked)
    }
  }

  return (
    <label className={`checkbox-toggle ${disabled ? 'checkbox-toggle--disabled' : ''}`}>
      <input
        type="checkbox"
        checked={checked}
        onChange={handleChange}
        disabled={disabled}
        className="checkbox-input"
      />
      <span className={`checkbox-box ${checked ? 'checkbox-box--checked' : ''} ${indeterminate ? 'checkbox-box--indeterminate' : ''}`}>
        {checked && !indeterminate && <i className="fa-duotone fa-check" />}
        {indeterminate && <i className="fa-duotone fa-minus" />}
      </span>
      {(label || description) && (
        <span className="checkbox-label-container">
          {label && <span className="checkbox-label">{label}</span>}
          {description && <span className="checkbox-description">{description}</span>}
        </span>
      )}
    </label>
  )
}

// Radio Toggle Group
interface RadioOption {
  value: string
  label: string
  description?: string
  disabled?: boolean
}

interface RadioToggleGroupProps {
  options: RadioOption[]
  value?: string
  onChange?: (value: string) => void
  name: string
  disabled?: boolean
  direction?: 'horizontal' | 'vertical'
}

export function RadioToggleGroup({
  options,
  value,
  onChange,
  name,
  disabled = false,
  direction = 'vertical'
}: RadioToggleGroupProps) {
  return (
    <div className={`radio-group radio-group--${direction}`}>
      {options.map(option => (
        <label 
          key={option.value} 
          className={`radio-toggle ${value === option.value ? 'radio-toggle--checked' : ''} ${option.disabled || disabled ? 'radio-toggle--disabled' : ''}`}
        >
          <input
            type="radio"
            name={name}
            value={option.value}
            checked={value === option.value}
            onChange={() => onChange?.(option.value)}
            disabled={option.disabled || disabled}
            className="radio-input"
          />
          <span className="radio-circle">
            {value === option.value && <span className="radio-dot" />}
          </span>
          <span className="radio-label-container">
            <span className="radio-label">{option.label}</span>
            {option.description && <span className="radio-description">{option.description}</span>}
          </span>
        </label>
      ))}
    </div>
  )
}
